export type DashboardPeriod = number;

export interface SeriesPoint {
  date: string;
  value: number;
}

export interface Metric {
  total: number;
  previous: number;
  series: number[];
}

export interface ActivityBreakdownItem {
  action: 'login' | 'module_view' | 'week_view' | 'material_view' | 'material_download';
  total: number;
  previous: number;
  series: number[];
}

export interface RankedItem {
  id: number;
  name: string;
  value: number;
}

export interface TopicScore {
  name: string;
  average: number;
}

export interface DashboardActivity {
  period: { days: DashboardPeriod; start: string; end: string };
  activeUsers: Metric;
  logins: Metric;
  contentViews: Metric;
  downloads: Metric;
  timeline: { current: SeriesPoint[]; previous: SeriesPoint[] };
  platform: { web: SeriesPoint[]; mobile: SeriesPoint[] };
  breakdown: ActivityBreakdownItem[];
  topModules: RankedItem[];
  topInstitutions: RankedItem[];
  usersByGroup: RankedItem[];
  accessByProfile: RankedItem[];
  topicScores: TopicScore[];
  heatmap: { date: string; value: number }[];
}

const seeded = (seed: number) => {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
};

const dayLabel = (date: Date) =>
  date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');

const buildSeries = (
  days: number,
  base: number,
  amplitude: number,
  rand: () => number,
  end: Date = new Date()
) => {
  const out: SeriesPoint[] = [];
  const today = end;
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const weekday = date.getDay();
    const weekendDip = weekday === 0 || weekday === 6 ? 0.45 : 1;
    const trend = 1 + ((days - i) / days) * 0.25;
    const noise = 0.9 + rand() * 0.2;
    const raw = base * weekendDip * trend * noise + (rand() - 0.5) * amplitude * 0.4;
    const prev = out.length ? out[out.length - 1].value : raw;
    out.push({
      date: dayLabel(date),
      value: Math.max(0, Math.round(prev * 0.55 + raw * 0.45)),
    });
  }
  return out;
};

const sum = (points: SeriesPoint[]) => points.reduce((acc, p) => acc + p.value, 0);

export const buildMockDashboard = (
  days: DashboardPeriod,
  from?: Date,
  to?: Date
): DashboardActivity => {
  const rand = seeded(days * 7919);
  let scale = 0.8;
  if (days <= 7) scale = 1;
  else if (days <= 31) scale = 0.9;

  const endDate = to ?? new Date();
  const current = buildSeries(days, 38 * scale, 14, rand, endDate);
  const previous = buildSeries(days, 31 * scale, 12, rand, endDate);
  const web = buildSeries(days, 22 * scale, 8, rand);
  const mobile = buildSeries(days, 16 * scale, 8, rand);

  const metric = (points: SeriesPoint[], prevFactor: number, sparkDays = 14): Metric => {
    const total = sum(points);
    return {
      total,
      previous: Math.round(total * prevFactor),
      series: points.slice(-sparkDays).map((p) => p.value),
    };
  };

  const logins = buildSeries(days, 14 * scale, 5, rand);
  const modules = buildSeries(days, 9 * scale, 4, rand);
  const weeks = buildSeries(days, 8 * scale, 4, rand);
  const materials = buildSeries(days, 11 * scale, 5, rand);
  const downloads = buildSeries(days, 4 * scale, 3, rand);

  const contentTotal = sum(modules) + sum(weeks) + sum(materials);

  const breakdown: ActivityBreakdownItem[] = [
    {
      action: 'login',
      total: sum(logins),
      previous: Math.round(sum(logins) * 0.88),
      series: logins.slice(-14).map((p) => p.value),
    },
    {
      action: 'module_view',
      total: sum(modules),
      previous: Math.round(sum(modules) * 0.79),
      series: modules.slice(-14).map((p) => p.value),
    },
    {
      action: 'week_view',
      total: sum(weeks),
      previous: Math.round(sum(weeks) * 1.06),
      series: weeks.slice(-14).map((p) => p.value),
    },
    {
      action: 'material_view',
      total: sum(materials),
      previous: Math.round(sum(materials) * 0.72),
      series: materials.slice(-14).map((p) => p.value),
    },
    {
      action: 'material_download',
      total: sum(downloads),
      previous: Math.round(sum(downloads) * 0.95),
      series: downloads.slice(-14).map((p) => p.value),
    },
  ];

  const end = to ?? new Date();
  const start = from ?? new Date(end.getTime() - (days - 1) * 86400000);

  const heatRand = seeded(4242);
  const heatmap: { date: string; value: number }[] = [];
  for (let i = 26 * 7 - 1; i >= 0; i -= 1) {
    const date = new Date(end);
    date.setDate(end.getDate() - i);
    const weekday = date.getDay();
    const weekendDip = weekday === 0 || weekday === 6 ? 0.2 : 1;
    const ramp = 0.4 + ((26 * 7 - i) / (26 * 7)) * 0.8;
    heatmap.push({
      date: date.toISOString().slice(0, 10),
      value: Math.max(0, Math.round(34 * weekendDip * ramp * (0.6 + heatRand() * 0.8))),
    });
  }

  return {
    period: { days, start: start.toISOString(), end: end.toISOString() },
    activeUsers: {
      total: Math.round(64 * scale),
      previous: Math.round(57 * scale),
      series: current.slice(-14).map((p) => Math.round(p.value / 2)),
    },
    logins: metric(logins, 0.88),
    contentViews: {
      total: contentTotal,
      previous: Math.round(contentTotal * 0.8),
      series: materials.slice(-14).map((p, i) => p.value + modules[modules.length - 14 + i].value),
    },
    downloads: metric(downloads, 0.95),
    timeline: { current, previous },
    platform: { web, mobile },
    breakdown,
    topModules: [
      { id: 1, name: '1º Ano', value: 148 },
      { id: 2, name: '2º Ano', value: 121 },
      { id: 3, name: '3º Ano', value: 97 },
      { id: 4, name: '4º Ano', value: 63 },
      { id: 5, name: '5º Ano', value: 41 },
    ],
    topInstitutions: [
      { id: 2, name: 'Instituição 2', value: 212 },
      { id: 1, name: 'Instituição x', value: 168 },
      { id: 4, name: 'Instituição 4', value: 131 },
      { id: 3, name: 'Instituição 3', value: 87 },
      { id: 5, name: 'Instituição 5', value: 42 },
    ],
    accessByProfile: [
      { id: 4, name: 'Professores', value: 418 },
      { id: 3, name: 'Tutores', value: 257 },
      { id: 5, name: 'Responsáveis', value: 173 },
      { id: 7, name: 'Instituições', value: 64 },
      { id: 6, name: 'Secretarias', value: 36 },
    ],
    heatmap,
    usersByGroup: [
      { id: 3, name: 'Tutores', value: 12 },
      { id: 4, name: 'Professores', value: 38 },
      { id: 5, name: 'Responsáveis', value: 96 },
      { id: 6, name: 'Secretarias', value: 4 },
      { id: 7, name: 'Instituições', value: 9 },
    ],
    topicScores: [
      { name: 'Afeto', average: 8.4 },
      { name: 'Validação', average: 7.9 },
      { name: 'Empatia', average: 7.6 },
      { name: 'Palavras', average: 7.1 },
      { name: 'Escuta', average: 6.8 },
      { name: 'Limites', average: 6.2 },
    ],
  };
};

export const actionLabels: Record<ActivityBreakdownItem['action'], string> = {
  login: 'Logins',
  module_view: 'Módulos abertos',
  week_view: 'Semanas abertas',
  material_view: 'Materiais visualizados',
  material_download: 'Materiais baixados',
};

export const deltaPercent = (total: number, previous: number) =>
  previous === 0 ? null : ((total - previous) / previous) * 100;

export const emptyDashboard = (days: number, from: Date, to: Date): DashboardActivity => {
  const empty = { total: 0, previous: 0, series: [] as number[] };
  return {
    period: { days, start: from.toISOString().slice(0, 10), end: to.toISOString().slice(0, 10) },
    activeUsers: { ...empty },
    logins: { ...empty },
    contentViews: { ...empty },
    downloads: { ...empty },
    timeline: { current: [], previous: [] },
    platform: { web: [], mobile: [] },
    breakdown: [],
    topModules: [],
    topInstitutions: [],
    usersByGroup: [],
    accessByProfile: [],
    topicScores: [],
    heatmap: [],
  };
};
